
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { auth, getUserInfo } from '@/services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { toast } from 'sonner';
import { MdLogout, MdDashboard, MdAdminPanelSettings } from 'react-icons/md';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, loading] = useAuthState(auth);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user) {
      // Check if user is admin
      getUserInfo(user.uid).then(userData => {
        if (userData?.isAdmin) {
          setIsAdmin(true);
        }
      });
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('referrerCode');
      navigate('/');
      toast.success('Successfully logged out');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to log out');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass-panel backdrop-blur-lg border-b border-white/10 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="https://solarinphoenix.com/solar-referral-program" className="text-2xl font-bold text-gradient">
            Solar In Phoenix
          </Link>
          
          {user && (
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button 
                  variant="ghost" 
                  className={`flex items-center space-x-2 ${location.pathname === '/dashboard' ? 'bg-secondary' : ''}`}
                >
                  <MdDashboard className="mr-1" />
                  <span>Dashboard</span>
                </Button>
              </Link>
              
              {isAdmin && (
                <Link to="/admin">
                  <Button 
                    variant="ghost" 
                    className={`flex items-center space-x-2 ${location.pathname === '/admin' ? 'bg-secondary' : ''}`}
                  >
                    <MdAdminPanelSettings className="mr-1" />
                    <span>Admin</span>
                  </Button>
                </Link>
              )}
              
              <Button 
                variant="ghost" 
                className="flex items-center text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={handleLogout}
              >
                <MdLogout className="mr-1" />
                <span>Logout</span>
              </Button>
            </div>
          )}
        </div>
      </header>
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          {children}
        </div>
      </main>
      
      <footer className="glass-panel bg-transparent backdrop-blur-md border-t border-white/10">
        <div className="container mx-auto px-4 py-6 text-center text-muted-foreground">
          <p>© {new Date().getFullYear()} Solar In Phoenix. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
